import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import * as XLSX from "xlsx";

// ── Design tokens — matches Landing ─────────────
const C = {
    bg: "#ffffff",
    surface: "#f7f7f7",
    border: "#e0e0e0",
    text: "#000000",
    muted: "#333333",
    dim: "#777777",
    accent: "#52658F",
    cyan: "#2a6a8a",
    green: "#059669",
};
const SYNE = "'Fraunces', serif";
const MONO = "'Plus Jakarta Sans', sans-serif";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

// ── Statistics Helpers ─────────────────────────────────
function mean(values) { return values.reduce((s, v) => s + v, 0) / values.length; }
function stdDev(values) { const m = mean(values); return Math.sqrt(values.reduce((s, v) => s + Math.pow(v - m, 2), 0) / values.length); }
function skewness(values) { const m = mean(values); const s = stdDev(values); if (s === 0) return 0; return values.reduce((sum, v) => sum + Math.pow((v - m) / s, 3), 0) / values.length; }
function kurtosis(values) { const m = mean(values); const s = stdDev(values); if (s === 0) return 0; return values.reduce((sum, v) => sum + Math.pow((v - m) / s, 4), 0) / values.length - 3; }
function percentile(sorted, p) { const idx = (p / 100) * (sorted.length - 1); const lower = Math.floor(idx); const upper = Math.ceil(idx); return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower); }
function isBimodal(values) { const sorted = [...values].sort((a, b) => a - b); const med = percentile(sorted, 50); const lower = sorted.filter(v => v <= med); const upper = sorted.filter(v => v > med); if (lower.length < 5 || upper.length < 5) return false; const lowerMean = mean(lower); const upperMean = mean(upper); const overallStd = stdDev(values); return Math.abs(upperMean - lowerMean) > overallStd * 1.5; }

function detectDistribution(values) {
    const skew = skewness(values);
    const kurt = kurtosis(values);
    const bimodal = isBimodal(values);
    if (bimodal) return { type: "bimodal", label: "Bimodal", description: "Your data has two distinct peaks — possibly two different groups mixed together.", method: "iqr_per_cluster", icon: "〰️" };
    if (Math.abs(skew) < 0.5 && Math.abs(kurt) < 1) return { type: "normal", label: "Normal", description: "Your data is roughly bell-shaped and symmetrically distributed.", method: "zscore", icon: "🔔" };
    if (skew > 1) return { type: "right_skewed", label: "Right-Skewed", description: "Most values are low with a few very high ones — common in sales and transaction data.", method: "iqr", icon: "📈" };
    if (skew < -1) return { type: "left_skewed", label: "Left-Skewed", description: "Most values are high with a few very low ones.", method: "iqr", icon: "📉" };
    return { type: "moderate", label: "Moderately Skewed", description: "Your data has a slight skew — we'll use a robust method to find outliers.", method: "iqr", icon: "📊" };
}

function runOutlierDetection(rows, col, distribution) {
    const parsed = rows.map((row, idx) => ({ row, idx, value: parseFloat(row[col]) })).filter(r => !isNaN(r.value));
    const values = parsed.map(r => r.value);
    const sorted = [...values].sort((a, b) => a - b);
    const q1 = percentile(sorted, 25); const q3 = percentile(sorted, 75); const iqr = q3 - q1;
    const lowerFence = q1 - 1.5 * iqr; const upperFence = q3 + 1.5 * iqr;
    const m = mean(values); const s = stdDev(values);
    return parsed.map(({ row, value }) => {
        const flags = []; const zScore = s > 0 ? Math.abs((value - m) / s) : 0;
        if (distribution.method === "zscore") { if (zScore > 3) flags.push({ type: "zscore_high", label: "Extreme Value (Z > 3)", severity: "high" }); else if (zScore > 2) flags.push({ type: "zscore_medium", label: "Unusual Value (Z > 2)", severity: "medium" }); }
        else { if (value < lowerFence) flags.push({ type: "iqr_low", label: "Unusually Low", severity: value < (q1 - 3 * iqr) ? "high" : "medium" }); if (value > upperFence) flags.push({ type: "iqr_high", label: "Unusually High", severity: value > (q3 + 3 * iqr) ? "high" : "medium" }); }
        if (value < 0) flags.push({ type: "negative", label: "Negative Value", severity: "high" });
        if (value === 0) flags.push({ type: "zero", label: "Zero Value", severity: "low" });
        return { row, value, zScore, flags, isOutlier: flags.length > 0 };
    });
}

function pearsonCorrelation(xValues, yValues) {
    if (xValues.length < 5) return 0;
    const mx = mean(xValues); const my = mean(yValues);
    const num = xValues.reduce((s, x, i) => s + (x - mx) * (yValues[i] - my), 0);
    const den = Math.sqrt(xValues.reduce((s, x) => s + Math.pow(x - mx, 2), 0) * yValues.reduce((s, y) => s + Math.pow(y - my, 2), 0));
    return den === 0 ? 0 : num / den;
}

function linearRegression(xValues, yValues) {
    const mx = mean(xValues); const my = mean(yValues);
    const slope = xValues.reduce((s, x, i) => s + (x - mx) * (yValues[i] - my), 0) / xValues.reduce((s, x) => s + Math.pow(x - mx, 2), 0);
    return { slope, intercept: my - slope * mx };
}

function runBivariateDetection(rows, targetCol, allHeaders) {
    const otherNumericCols = allHeaders.filter(h => { if (h === targetCol) return false; return rows.slice(0, 20).filter(r => !isNaN(parseFloat(r[h])) && r[h] !== "").length >= 10; });
    if (otherNumericCols.length === 0) return {};
    const targetValues = rows.map(r => parseFloat(r[targetCol]));
    const bivariateResults = {};
    otherNumericCols.forEach(col => {
        const paired = rows.map((r, i) => ({ row: r, x: parseFloat(r[col]), y: targetValues[i], idx: i })).filter(p => !isNaN(p.x) && !isNaN(p.y));
        if (paired.length < 10) return;
        const xs = paired.map(p => p.x); const ys = paired.map(p => p.y);
        const trimPct = 0.05; const trimCount = Math.floor(paired.length * trimPct);
        const sortedByX = [...paired].sort((a, b) => a.x - b.x).slice(trimCount, paired.length - trimCount);
        const r = pearsonCorrelation(sortedByX.map(p => p.x), sortedByX.map(p => p.y));
        if (Math.abs(r) < 0.3) return;
        const { slope, intercept } = linearRegression(xs, ys);
        const residuals = paired.map(p => ({ row: p.row, x: p.x, y: p.y, predicted: slope * p.x + intercept, residual: p.y - (slope * p.x + intercept) }));
        const residualValues = residuals.map(r => r.residual); const sortedRes = [...residualValues].sort((a, b) => a - b);
        const rq1 = percentile(sortedRes, 25); const rq3 = percentile(sortedRes, 75); const riqr = rq3 - rq1;
        const lowerFence = rq1 - 1.5 * riqr; const upperFence = rq3 + 1.5 * riqr;
        const flagged = residuals.filter(r => r.residual < lowerFence || r.residual > upperFence).map(r => {
            const direction = r.residual > 0 ? "higher" : "lower";
            return { row: r.row, x: r.x, y: r.y, predicted: r.predicted, residual: r.residual, direction, magnitude: Math.abs(r.residual), correlation: r, description: `${targetCol} is ${direction} than expected given ${col} value of ${Number(r.x).toLocaleString("id-ID")} (expected ~${Number(r.predicted.toFixed(0)).toLocaleString("id-ID")}, actual ${Number(r.y.toFixed(0)).toLocaleString("id-ID")})`, severity: Math.abs(r.residual) > Math.abs(upperFence) * 2 ? "high" : "medium" };
        });
        if (flagged.length > 0) bivariateResults[col] = { correlation: r, direction: r > 0 ? "positive" : "negative", slope, intercept, flagged };
    });
    return bivariateResults;
}

const SAMPLE_OUTLIER_DATA = {
    headers: ["store_id", "area_code", "store_type", "monthly_sales", "transactions", "active_days"],
    rows: [
        { store_id: "STR-0001", area_code: "JKT-S", store_type: "Minimarket", monthly_sales: "850000", transactions: "89", active_days: "28" },
        { store_id: "STR-0002", area_code: "JKT-T", store_type: "Warung", monthly_sales: "320000", transactions: "38", active_days: "25" },
        { store_id: "STR-0003", area_code: "JKT-B", store_type: "Supermarket", monthly_sales: "2100000", transactions: "198", active_days: "30" },
        { store_id: "STR-0004", area_code: "JKT-U", store_type: "Sekolah", monthly_sales: "450000", transactions: "52", active_days: "22" },
        { store_id: "STR-0005", area_code: "TNG", store_type: "Pasar", monthly_sales: "780000", transactions: "81", active_days: "27" },
        { store_id: "STR-0006", area_code: "JKT-S", store_type: "Minimarket", monthly_sales: "45000000", transactions: "12", active_days: "28" },
        { store_id: "STR-0007", area_code: "BKS", store_type: "Warung", monthly_sales: "290000", transactions: "33", active_days: "24" },
        { store_id: "STR-0008", area_code: "JKT-T", store_type: "Minimarket", monthly_sales: "920000", transactions: "95", active_days: "29" },
        { store_id: "STR-0009", area_code: "JKT-B", store_type: "Sekolah", monthly_sales: "5500", transactions: "2", active_days: "26" },
        { store_id: "STR-0010", area_code: "JKT-U", store_type: "Pasar", monthly_sales: "1200000", transactions: "118", active_days: "30" },
        { store_id: "STR-0011", area_code: "TNG", store_type: "Supermarket", monthly_sales: "3200000", transactions: "287", active_days: "30" },
        { store_id: "STR-0012", area_code: "JKT-S", store_type: "Warung", monthly_sales: "380000", transactions: "44", active_days: "23" },
        { store_id: "STR-0013", area_code: "BKS", store_type: "Minimarket", monthly_sales: "38000000", transactions: "8", active_days: "29" },
        { store_id: "STR-0014", area_code: "JKT-T", store_type: "Sekolah", monthly_sales: "670000", transactions: "71", active_days: "26" },
        { store_id: "STR-0015", area_code: "JKT-B", store_type: "Pasar", monthly_sales: "990000", transactions: "103", active_days: "28" },
        { store_id: "STR-0016", area_code: "JKT-U", store_type: "Minimarket", monthly_sales: "1450000", transactions: "142", active_days: "30" },
        { store_id: "STR-0017", area_code: "TNG", store_type: "Warung", monthly_sales: "8000", transactions: "3", active_days: "25" },
        { store_id: "STR-0018", area_code: "JKT-S", store_type: "Supermarket", monthly_sales: "2800000", transactions: "251", active_days: "30" },
        { store_id: "STR-0019", area_code: "BKS", store_type: "Minimarket", monthly_sales: "-150000", transactions: "45", active_days: "27" },
        { store_id: "STR-0020", area_code: "JKT-T", store_type: "Pasar", monthly_sales: "0", transactions: "0", active_days: "30" },
        { store_id: "STR-0021", area_code: "JKT-B", store_type: "Warung", monthly_sales: "410000", transactions: "48", active_days: "24" },
        { store_id: "STR-0022", area_code: "JKT-U", store_type: "Sekolah", monthly_sales: "760000", transactions: "79", active_days: "27" },
        { store_id: "STR-0023", area_code: "TNG", store_type: "Minimarket", monthly_sales: "1100000", transactions: "109", active_days: "29" },
        { store_id: "STR-0024", area_code: "JKT-S", store_type: "Supermarket", monthly_sales: "28000000", transactions: "6", active_days: "28" },
        { store_id: "STR-0025", area_code: "BKS", store_type: "Pasar", monthly_sales: "880000", transactions: "91", active_days: "26" },
    ]
};

function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
        /^\*\*[^*]+\*\*$/.test(part)
            ? <strong key={i}>{part.slice(2, -2)}</strong>
            : part
    );
}

function renderMarkdown(text) {
    return text
        .split("\n")
        .map((line, i) => {
            if (/^### (.+)/.test(line))  return <h4 key={i} style={{ margin: "0.75rem 0 0.25rem", color: C.text, fontFamily: SYNE, fontSize: "0.85rem" }}>{line.replace(/^### /, "")}</h4>;
            if (/^## (.+)/.test(line))   return <h3 key={i} style={{ margin: "0.75rem 0 0.25rem", color: C.text, fontFamily: SYNE, fontSize: "0.9rem" }}>{line.replace(/^## /, "")}</h3>;
            if (/^- (.+)/.test(line))    return <div key={i} style={{ paddingLeft: "0.75rem", marginBottom: "0.25rem" }}>• {renderInline(line.replace(/^- /, ""))}</div>;
            if (line.trim() === "")      return <div key={i} style={{ height: "0.5rem" }} />;
            return <div key={i} style={{ marginBottom: "0.2rem" }}>{renderInline(line)}</div>;
        });
}

export default function OutlierPage() {
    const navigate = useNavigate();
    const fileRef = useRef();

    const [step, setStep] = useState("upload");
    const [fileName, setFileName] = useState("");
    const [data, setData] = useState(null);
    const [selectedCol, setSelectedCol] = useState("");
    const [distribution, setDistribution] = useState(null);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [aiResponse, setAiResponse] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);
    const [explanations, setExplanations] = useState({});

    useEffect(() => {
        if (!document.querySelector('link[href*="Fraunces"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet"; link.href = FONT_LINK;
            document.head.appendChild(link);
        }
    }, []);

    async function handleFile(e) {
        const file = e.target.files[0]; if (!file) return;
        setFileName(file.name);
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, dateNF: "yyyy-mm-dd", defval: "" });
        const headers = raw[0].map(h => String(h || "").trim()).filter(h => h !== "");
        const rows = raw.slice(1).filter(row => row.some(cell => cell !== "" && cell !== null)).map(row => { const obj = {}; headers.forEach((h, i) => { obj[h] = String(row[i] || "").trim(); }); return obj; });
        setData({ headers, rows });
        const firstNumeric = headers.find(h => rows.slice(0, 10).filter(r => !isNaN(parseFloat(r[h])) && r[h] !== "").length >= 3);
        if (firstNumeric) setSelectedCol(firstNumeric);
        setStep("configure");
    }

    function runAnalysis(analysisData, col) {
        const values = analysisData.rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
        const dist = detectDistribution(values);
        setDistribution(dist);
        const flagged = runOutlierDetection(analysisData.rows, col, dist);
        const univariateOutliers = flagged.filter(r => r.isOutlier);
        const bivariateResults = runBivariateDetection(analysisData.rows, col, analysisData.headers);
        const bivariateOnlyRows = [];
        Object.entries(bivariateResults).forEach(([col2, result]) => {
            result.flagged.forEach(item => {
                const firstKey = Object.keys(item.row)[0];
                if (!univariateOutliers.some(u => u.row[firstKey] === item.row[firstKey])) {
                    bivariateOnlyRows.push({ row: item.row, value: item.y, zScore: 0, flags: [], isOutlier: true, bivariateFlags: [{ col: col2, ...item }] });
                }
            });
        });
        const enrichedOutliers = univariateOutliers.map(item => {
            const firstKey = Object.keys(item.row)[0];
            const bivariateFlags = [];
            Object.entries(bivariateResults).forEach(([col2, result]) => { const match = result.flagged.find(f => f.row[firstKey] === item.row[firstKey]); if (match) bivariateFlags.push({ col: col2, ...match }); });
            return { ...item, bivariateFlags };
        });
        const allOutliers = [...enrichedOutliers, ...bivariateOnlyRows].sort((a, b) => b.zScore - a.zScore);
        const allValues = flagged.map(r => r.value);
        const sorted = [...allValues].sort((a, b) => a - b);
        return { outliers: allOutliers, bivariateResults, total: flagged.length, mean: mean(allValues).toFixed(2), stdDev: stdDev(allValues).toFixed(2), q1: percentile(sorted, 25).toFixed(2), q3: percentile(sorted, 75).toFixed(2), min: sorted[0].toFixed(2), max: sorted[sorted.length - 1].toFixed(2), dist };
    }

    function handleAnalyze() {
        setLoading(true);
        const { dist, ...res } = runAnalysis(data, selectedCol);
        setDistribution(dist);
        setResults(res);
        setLoading(false);
        setStep("results");
    }

    function loadSampleOutlier() {
        setFileName("sample_sales_data.xlsx");
        setData(SAMPLE_OUTLIER_DATA);
        setSelectedCol("monthly_sales");
        const { dist, ...res } = runAnalysis(SAMPLE_OUTLIER_DATA, "monthly_sales");
        setDistribution(dist);
        setResults(res);
        setStep("results");
    }

    async function analyzeRow(item) {
        const firstKey = Object.keys(item.row)[0];
        const rowKey = item.row[firstKey] + "_" + item.value;

        // Load from cache if already explained this session
        if (explanations[rowKey]) {
            setSelectedRow(item);
            setAiResponse(explanations[rowKey]);
            setAiError(null);
            return;
        }

        setSelectedRow(item); setAiResponse(""); setAiError(null); setAiLoading(true);
        const severity = item.flags.some(f => f.severity === "high") ? "high" : item.flags.some(f => f.severity === "medium") ? "medium" : "low";
        const bivariateContext = item.bivariateFlags && item.bivariateFlags.length > 0 ? item.bivariateFlags.map(f => `- vs ${f.col} (correlation: ${f.correlation?.toFixed ? f.correlation.toFixed(2) : "N/A"}): ${f.description}`).join("\n") : null;
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/explain-outlier", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ store_name: item.row[firstKey] || "Unknown", column_name: selectedCol, value: item.value, mean: parseFloat(results.mean), std_dev: parseFloat(results.stdDev), q1: parseFloat(results.q1), q3: parseFloat(results.q3), iqr: parseFloat(results.q3) - parseFloat(results.q1), flags: item.flags.map(f => f.label), severity, distribution_type: distribution.type, distribution_label: distribution.label, bivariate_context: bivariateContext })
            });
            if (!response.ok) { const errData = await response.json(); throw new Error(errData.detail || "Server error: " + response.status); }
            const responseData = await response.json();
            setAiResponse(responseData.explanation);
            setExplanations(prev => ({ ...prev, [rowKey]: responseData.explanation }));
        } catch (err) { setAiError(err.message); }
        setAiLoading(false);
    }

    async function exportOutlierPDF() {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/export-outlier-pdf", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ file_name: fileName, column_name: selectedCol, distribution_label: distribution.label, distribution_description: distribution.description, method: distribution.method, total_rows: results.total, outlier_count: results.outliers.length, mean: results.mean, std_dev: results.stdDev, q1: results.q1, q3: results.q3, outliers: results.outliers.map(item => ({ identifier: item.row[Object.keys(item.row)[0]] || "Unknown", value: item.value, z_score: item.zScore, flags: item.flags.map(f => f.label), severity: item.flags.some(f => f.severity === "high") ? "high" : item.flags.some(f => f.severity === "medium") ? "medium" : "low" })) })
            });
            if (!response.ok) throw new Error("Export failed");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "dgb-outlier-report.pdf"; a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) { alert("Export failed: " + err.message); }
    }

    const severityColor = s => s === "high" ? "#c0392b" : s === "medium" ? "#b45309" : C.accent;
    const severityBg = s => s === "high" ? "#fde8e8" : s === "medium" ? "#fff8e1" : "#eef1f7";

    return (
        <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: MONO, overflowX: "hidden" }}>
            <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>

            {/* Navbar — black, matches landing */}
            <nav style={{ background: "#000000", padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
                <img src={require("../assets/dgb-logo-dark.png")} alt="DGB" style={{ height: 80, cursor: "pointer", marginLeft: "-16px", marginTop: "-4px", marginBottom: "-4px" }} onClick={() => navigate("/")} />
                <div style={{ display: "flex", gap: "0.75rem" }}>
                    {step !== "upload" && (
                        <button onClick={() => { setStep("upload"); setData(null); setResults(null); setDistribution(null); setExplanations({}); }}
                            style={{ background: "transparent", border: "1px solid #333", borderRadius: 6, padding: "0.35rem 1rem", color: "#888", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                            ← New Analysis
                        </button>
                    )}
                    <button onClick={() => navigate("/app")}
                        style={{ background: "transparent", border: "none", color: "#888", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                        Switch to Reconciliation
                    </button>
                    <button onClick={async () => { await supabase.auth.signOut(); navigate("/"); }}
                        style={{ background: C.accent, border: "none", borderRadius: 6, padding: "0.35rem 1rem", color: "white", fontFamily: MONO, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
                        Log out
                    </button>
                </div>
            </nav>

            {/* Upload Step */}
            {step === "upload" && (
                <div style={{ maxWidth: 600, margin: "0 auto", padding: "5rem 2rem", textAlign: "center" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#ECDBC9", border: "1px solid #d8c4a8", borderRadius: 100, padding: "4px 14px", marginBottom: "1.5rem" }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent }} />
                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#5a3a1a", letterSpacing: "0.06em" }}>OUTLIER DETECTION</span>
                    </div>
                    <h1 style={{ fontFamily: SYNE, fontSize: "clamp(2rem,4.5vw,2.8rem)", fontWeight: 800, color: C.text, marginBottom: "0.75rem", letterSpacing: "-0.02em" }}>
                        Find anomalies in your data
                    </h1>
                    <p style={{ color: C.muted, marginBottom: "0.5rem", fontSize: "1rem", lineHeight: 1.8 }}>
                        Upload any data file — DGB detects the distribution and finds anomalies automatically.
                    </p>
                    <p style={{ color: C.dim, fontSize: "0.85rem", marginBottom: "3rem" }}>
                        Works for sales data, transactions, fraud analysis, and more.
                    </p>
                    <div onClick={() => fileRef.current && fileRef.current.click()}
                        style={{ border: "1.5px dashed " + C.border, borderRadius: 14, padding: "3rem", cursor: "pointer", background: C.surface, transition: "border-color 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
                        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                        <input type="file" accept=".csv,.xlsx,.xls" ref={fileRef} style={{ display: "none" }} onChange={handleFile} />
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "0.75rem" }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        <div style={{ color: C.muted, fontSize: "0.9rem", fontWeight: 500 }}>{fileName || "Click to upload CSV or Excel"}</div>
                        <div style={{ color: C.dim, fontSize: "0.75rem", marginTop: "0.35rem" }}>CSV, XLSX, XLS supported</div>
                    </div>
                    <button onClick={loadSampleOutlier}
                        style={{ background: "transparent", border: "none", color: C.accent, fontFamily: MONO, fontSize: "0.82rem", cursor: "pointer", marginTop: "1.25rem", fontWeight: 600 }}>
                        or load sample data →
                    </button>
                </div>
            )}

            {/* Configure Step */}
            {step === "configure" && data && (
                <div style={{ maxWidth: 640, margin: "0 auto", padding: "3rem 2rem" }}>
                    <h1 style={{ fontFamily: SYNE, fontSize: "1.9rem", fontWeight: 800, color: C.text, marginBottom: "0.5rem", letterSpacing: "-0.02em" }}>Which column to analyse?</h1>
                    <p style={{ color: C.muted, marginBottom: "2rem", lineHeight: 1.7 }}>
                        {data.rows.length} rows loaded from <strong>{fileName}</strong>. Select a numeric column — DGB will detect its distribution and choose the right method automatically.
                    </p>
                    <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.25rem", marginBottom: "1.5rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                            {data.headers.map(h => {
                                const numericCount = data.rows.slice(0, 20).filter(r => !isNaN(parseFloat(r[h])) && r[h] !== "").length;
                                const isNumeric = numericCount >= 3;
                                const isSelected = selectedCol === h;
                                return (
                                    <div key={h} onClick={() => isNumeric && setSelectedCol(h)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.75rem 1rem", borderRadius: 8, border: "1.5px solid " + (isSelected ? C.accent : C.border), background: isSelected ? "#eef1f7" : "white", cursor: isNumeric ? "pointer" : "not-allowed", opacity: isNumeric ? 1 : 0.4 }}>
                                        <span style={{ fontSize: "0.88rem", color: C.text, fontWeight: isSelected ? 600 : 400 }}>{h}</span>
                                        <span style={{ fontSize: "0.72rem", color: isNumeric ? (isSelected ? C.accent : C.dim) : C.dim, fontWeight: 500 }}>
                                            {isNumeric ? "numeric" : "text"}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <button disabled={!selectedCol || loading} onClick={handleAnalyze}
                        style={{ width: "100%", background: selectedCol ? C.accent : C.grey, color: selectedCol ? "white" : C.dim, border: "none", padding: "0.9rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", cursor: selectedCol ? "pointer" : "not-allowed", letterSpacing: "-0.01em" }}>
                        {loading ? "Detecting distribution..." : "Analyse →"}
                    </button>
                </div>
            )}

            {/* Results Step */}
            {step === "results" && results && distribution && (
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "2rem 2rem" }}>

                    {/* Distribution card */}
                    <div style={{ background: "#eef1f7", border: "1px solid " + C.accent + "44", borderRadius: 14, padding: "1.25rem 1.5rem", marginBottom: "1.5rem", display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                        <div style={{ fontSize: "2rem" }}>{distribution.icon}</div>
                        <div>
                            <div style={{ fontSize: "0.65rem", color: C.accent, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.25rem" }}>DISTRIBUTION DETECTED</div>
                            <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1.05rem", color: C.text, marginBottom: "0.25rem", letterSpacing: "-0.01em" }}>
                                {distribution.label} — using {distribution.method === "zscore" ? "Z-Score method" : "IQR method"}
                            </div>
                            <div style={{ fontSize: "0.85rem", color: C.muted }}>{distribution.description}</div>
                        </div>
                    </div>

                    {/* Summary cards */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
                        {[
                            { label: "OUTLIERS FOUND", value: String(results.outliers.length), color: results.outliers.length > 0 ? "#b45309" : C.green, sub: `out of ${results.total} rows` },
                            { label: "OUTLIER RATE", value: `${((results.outliers.length / results.total) * 100).toFixed(1)}%`, color: results.outliers.length > 0 ? "#b45309" : C.green, sub: "of total rows" },
                            { label: "AVERAGE", value: Number(results.mean).toLocaleString("id-ID"), color: C.accent, sub: selectedCol },
                            { label: "RANGE", value: `${Number(results.min).toLocaleString("id-ID")} – ${Number(results.max).toLocaleString("id-ID")}`, color: C.cyan, sub: "min – max" },
                        ].map((card, i) => (
                            <div key={i} style={{ background: "white", border: "1px solid " + C.border, borderRadius: 12, padding: "1.25rem" }}>
                                <div style={{ fontSize: "0.65rem", color: C.dim, letterSpacing: "0.1em", marginBottom: "0.5rem", fontFamily: MONO, fontWeight: 600 }}>{card.label}</div>
                                <div style={{ fontSize: "1.4rem", fontWeight: 700, color: card.color, fontFamily: SYNE, letterSpacing: "-0.02em" }}>{card.value}</div>
                                <div style={{ fontSize: "0.72rem", color: C.dim, marginTop: "0.25rem" }}>{card.sub}</div>
                            </div>
                        ))}
                    </div>

                    {/* Table + AI Panel */}
                    <div style={{ display: "flex", gap: "1rem" }}>
                        {/* Table */}
                        <div style={{ background: "white", border: "1px solid " + C.border, borderRadius: 14, overflow: "hidden", flex: 1 }}>
                            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid " + C.border, display: "flex", justifyContent: "space-between", alignItems: "center", background: C.surface }}>
                                <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em" }}>
                                    FLAGGED ROWS — {selectedCol}
                                </div>
                                <button onClick={exportOutlierPDF}
                                    style={{ background: "white", border: "1px solid " + C.border, borderRadius: 6, padding: "0.3rem 0.8rem", fontFamily: MONO, fontSize: "0.72rem", color: C.muted, cursor: "pointer" }}>
                                    ↓ Export PDF
                                </button>
                            </div>

                            {results.outliers.length === 0 ? (
                                <div style={{ padding: "3rem", textAlign: "center", color: C.dim }}>
                                    <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>✓</div>
                                    <div style={{ fontFamily: SYNE, fontWeight: 700, color: C.green, fontSize: "1rem" }}>No outliers detected</div>
                                    <div style={{ fontSize: "0.85rem", color: C.dim, marginTop: "0.4rem" }}>Your data looks clean.</div>
                                </div>
                            ) : (
                                <div style={{ overflowX: "auto" }}>
                                    <div style={{ minWidth: 700 }}>
                                        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: "0.5rem", padding: "0.6rem 1.25rem", borderBottom: "1px solid " + C.border, background: C.surface }}>
                                            {["Row", selectedCol, "Z-Score", "Flags", "Severity"].map(h => (
                                                <div key={h} style={{ fontSize: "0.65rem", color: C.dim, letterSpacing: "0.08rem", fontFamily: MONO, fontWeight: 600 }}>{h.toUpperCase()}</div>
                                            ))}
                                        </div>
                                        <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
                                            {results.outliers.map((item, i) => {
                                                const sev = item.flags.some(f => f.severity === "high") ? "high" : item.flags.some(f => f.severity === "medium") ? "medium" : "low";
                                                const firstKey = Object.keys(item.row)[0];
                                                const rowKey = item.row[firstKey] + "_" + item.value;
                                                const isSelected = selectedRow && Object.keys(selectedRow.row)[0] === firstKey && selectedRow.row[firstKey] === item.row[firstKey];
                                                const isExplained = !!explanations[rowKey];
                                                return (
                                                    <div key={i} onClick={() => analyzeRow(item)}
                                                        style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: "0.5rem", padding: "0.75rem 1.25rem", borderBottom: "1px solid " + C.border, background: isSelected ? "#eef1f7" : "white", borderLeft: isSelected ? "3px solid " + C.accent : "3px solid transparent", cursor: "pointer" }}>
                                                        <div style={{ fontSize: "0.82rem", color: C.text, fontWeight: 600 }}>
                                                            {item.row[firstKey] || `Row ${i + 1}`}
                                                            {isExplained && <span style={{ marginLeft: "0.4rem", fontSize: "0.65rem", color: C.green, fontWeight: 500 }}>✓</span>}
                                                        </div>
                                                        <div style={{ fontSize: "0.82rem", color: severityColor(sev), fontWeight: 600 }}>{Number(item.value).toLocaleString("id-ID")}</div>
                                                        <div style={{ fontSize: "0.78rem", color: C.muted }}>{item.zScore.toFixed(2)}σ</div>
                                                        <div style={{ fontSize: "0.72rem", color: C.muted, lineHeight: 1.5 }}>
                                                            {item.flags.map(f => f.label).join(", ")}
                                                            {item.bivariateFlags && item.bivariateFlags.length > 0 && (
                                                                <div style={{ marginTop: "0.2rem", color: C.cyan, fontSize: "0.65rem" }}>
                                                                    {item.bivariateFlags.map(f => `vs ${f.col}`).join(", ")}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: "0.65rem", padding: "0.2rem 0.5rem", borderRadius: 4, background: severityBg(sev), color: severityColor(sev), fontWeight: 600 }}>
                                                                {sev.charAt(0).toUpperCase() + sev.slice(1)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* AI Panel */}
                        {selectedRow && (
                            <div style={{ background: "white", border: "1px solid " + C.border, borderRadius: 14, padding: "1.25rem", width: 340, flexShrink: 0, display: "flex", flexDirection: "column", gap: "1rem", maxHeight: "60vh", overflowY: "auto" }}>
                                <div>
                                    <div style={{ fontSize: "0.65rem", color: C.accent, letterSpacing: "0.12rem", fontFamily: MONO, fontWeight: 700, marginBottom: "0.5rem" }}>AI ANALYSIS</div>
                                    <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", color: C.text, letterSpacing: "-0.01em" }}>
                                        {selectedRow.row[Object.keys(selectedRow.row)[0]] || "Unknown"}
                                    </div>
                                    <div style={{ fontSize: "0.78rem", color: C.dim, marginTop: "0.25rem" }}>
                                        {selectedCol}: {Number(selectedRow.value).toLocaleString("id-ID")}
                                    </div>
                                </div>

                                <div style={{ background: C.surface, borderRadius: 8, padding: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                                    {selectedRow.flags.map((f, i) => (
                                        <span key={i} style={{ fontSize: "0.65rem", padding: "0.2rem 0.5rem", borderRadius: 4, background: severityBg(f.severity), color: severityColor(f.severity), fontWeight: 600 }}>
                                            {f.label}
                                        </span>
                                    ))}
                                </div>

                                <div style={{ fontSize: "0.85rem", color: C.muted, lineHeight: 1.7 }}>
                                    {aiLoading ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                            {[1, 2, 3].map(i => <div key={i} style={{ height: 14, borderRadius: 4, background: "linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite", width: i === 3 ? "60%" : "100%" }} />)}
                                        </div>
                                    ) : aiError ? (
                                        <div style={{ color: "#c0392b", fontSize: "0.82rem" }}>⚠ {aiError}</div>
                                    ) : aiResponse ? (
                                        <div style={{ fontSize: "0.85rem", color: C.muted, lineHeight: 1.7 }}>
                                            {renderMarkdown(aiResponse)}
                                        </div>
                                    ) : (
                                        <span style={{ fontStyle: "italic", color: C.dim }}>Click a row to get AI analysis.</span>
                                    )}
                                </div>

                                {selectedRow.bivariateFlags && selectedRow.bivariateFlags.length > 0 && (
                                    <div style={{ background: "#e8f0f8", borderRadius: 8, padding: "0.75rem", border: "1px solid " + C.accent + "44" }}>
                                        <div style={{ fontSize: "0.65rem", color: C.cyan, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.5rem" }}>RELATIONSHIP ANOMALIES</div>
                                        {selectedRow.bivariateFlags.map((f, i) => (
                                            <div key={i} style={{ fontSize: "0.78rem", color: C.muted, marginBottom: "0.4rem", lineHeight: 1.5 }}>
                                                <span style={{ color: C.cyan, fontWeight: 600 }}>vs {f.col}</span> — {f.description}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
